/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import omit from 'lodash/omit';
import { compose } from 'recompose';

import { buildHashWithAuthMode, getLocationHash } from 'js/lib/authModeDeepLinkUtils';
import type { InjectedRouter } from 'js/lib/connectToRouter';
import connectToRouter from 'js/lib/connectToRouter';
import deferToClientSideRender from 'js/lib/deferToClientSideRender';

import { Button, Typography, breakpoints } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import TrackedButton from 'bundles/page/components/TrackedButton';
import useUnifiedDiscoveryTrackingProperties from 'bundles/unified-common/hooks/useUnifiedDiscoveryTrackingProperties';

import _t from 'i18n!nls/browse';

type Props = {
  router: InjectedRouter;
};

const styles = {
  ctaBanner: css`
    .browse-cta-banner {
      height: 320px;
      width: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: column;
      background-color: var(--cds-color-blue-950);

      .banner-description {
        max-width: 1000px;
        text-align: center;
        color: var(--cds-color-neutral-primary-invert);
      }

      .join-free-button {
        margin-top: 16px;
        display: block;
        width: 220px;
        height: 48px;
        font-size: 16px;
        border-radius: 3px;
      }
    }

    ${breakpoints.down('sm')} {
      .browse-cta-banner {
        min-height: 320px;
        padding: 28px 20px;
        height: auto;
      }
    }
  `,
};

export const CTABanner = ({ router }: Props) => {
  const trackEvent = useTracker();
  const unifiedDiscoveryTrackingProperties = useUnifiedDiscoveryTrackingProperties();
  const renderCTABannerContent = () => {
    const handleOnClick = () => {
      trackEvent('click_button', {
        button: {
          name: 'signup' as const,
        },
        pageSection: { sectionName: 'bottom_banner' },
        ...unifiedDiscoveryTrackingProperties,
      });
      router.replace({
        pathname: router.location.pathname,
        query: omit({ ...router.location.query }, 'authMode'),
        hash: buildHashWithAuthMode(getLocationHash(router.location), 'signup'),
      });
    };
    return (
      <section className="browse-cta-banner">
        <Typography className="banner-description" variant="h1" component="h3">
          {_t('Join for free and get personalized recommendations, updates and offers.')}
        </Typography>
        <Button
          variant="primary"
          component={TrackedButton}
          type="button"
          trackingName="join_free"
          withVisibilityTracking={false}
          requireFullyVisible={false}
          className="join-free-button"
          onClick={handleOnClick}
        >
          {_t('Join for free')}
        </Button>
      </section>
    );
  };

  return (
    <div className="rc-CTABanner" css={styles.ctaBanner}>
      {renderCTABannerContent()}
    </div>
  );
};

export default compose<Props, {}>(
  deferToClientSideRender,
  connectToRouter<Props, Omit<Props, 'router'>>((router) => ({ router }))
)(CTABanner);
